import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, useInView } from 'framer-motion';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { 
  FileText, 
  Download,
  User,
  TrendingUp,
  Shield,
  Target,
  AlertCircle,
  CheckCircle,
  DollarSign,
  ArrowLeft,
  PieChart,
  ChevronDown,
  ChevronUp,
  Loader2
} from 'lucide-react';

function useIsMobile() {
  const [m, setM] = useState(false);
  useEffect(() => { const c = () => setM(window.innerWidth < 768); c(); window.addEventListener('resize', c); return () => window.removeEventListener('resize', c); }, []);
  return m;
}

function AnimatedHealthBar({ score }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  return (
    <div ref={ref} className="mt-4 w-full bg-gray-200 dark:bg-gray-800/50 rounded-full h-3 overflow-hidden">
      <motion.div
        className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-cyan-400"
        initial={{ width: 0 }}
        animate={isInView ? { width: `${score}%` } : { width: 0 }}
        transition={{ duration: 1.2, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.3 }}
      />
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ViewProfessionalPlan({ user, onLogout }) {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedGoals, setExpandedGoals] = useState({});
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchPlan();
  }, [planId]);

  const fetchPlan = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/advisor/plan/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlan(response.data.plan);
    } catch (error) {
      console.error('Error fetching plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'HIGH': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      'MEDIUM': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      'LOW': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
    };
    return colors[priority] || colors.MEDIUM;
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloadingPdf(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/advisor/plan/${planId}/download-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Financial_Plan_${plan.client_name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 lg:p-8">
        <Navigation user={user} onLogout={onLogout} />
        <div data-testid="plan-view-skeleton" className="max-w-7xl mx-auto animate-pulse">
          <div className="h-10 w-56 bg-gray-800 rounded-lg mb-2" />
          <div className="h-5 w-40 bg-gray-800/60 rounded mb-8" />
          <div className="h-28 bg-gray-800/40 rounded-xl mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {[...Array(2)].map((_, i) => <div key={i} className="h-48 bg-gray-800/40 rounded-xl" />)}
          </div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-gray-800/40 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen p-4 lg:p-8">
        <Navigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto">
          <Card className="glass p-12 dark:bg-gray-800 text-center">
            <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">Plan Not Found</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">The requested plan could not be found.</p>
            <Button onClick={() => navigate('/advisor/clients')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Clients
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="view-plan-page" className="min-h-screen p-4 lg:p-8 relative overflow-hidden">
      <Navigation user={user} onLogout={onLogout} />
      {!isMobile && (
        <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
          <motion.div className="absolute w-[400px] h-[400px] rounded-full bg-gradient-to-r from-blue-600/[0.05] to-indigo-500/[0.05] blur-3xl" animate={{ x: [0, 25, 0], y: [0, -20, 0] }} transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }} style={{ top: '5%', right: '5%' }} />
        </div>
      )}
      <motion.div className="max-w-7xl mx-auto relative z-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
        
        {/* Header */}
        <motion.div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <motion.div className="w-2 h-8 rounded-full bg-gradient-to-b from-blue-500 to-cyan-400" initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 0.4, delay: 0.2 }} />
              <div>
                <h1 data-testid="plan-header" className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100">
                  <span className="bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">Financial</span> Plan
                </h1>
                <p className="text-gray-600 dark:text-gray-400 text-sm">{plan.client_name}</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button 
              variant="outline" 
              onClick={() => navigate('/advisor/clients')}
              className="w-full sm:w-auto"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
              onClick={handleDownloadPDF}
              disabled={downloadingPdf}
            >
              {downloadingPdf ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating PDF...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </>
              )}
            </Button>
          </div>
        </motion.div>

        {/* Financial Health Score */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: isMobile ? 0 : 0.15 }}>
          <Card data-testid="health-score-card" className="p-6 border-gray-200 dark:border-gray-800/60 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">Financial Health Score</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">{plan.health_rating}</p>
              </div>
              <div className="text-right">
                <motion.div className="text-5xl font-bold bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent" initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.3, type: "spring", stiffness: 200 }}>{plan.financial_health_score}</motion.div>
                <div className="text-xs text-gray-500">out of 100</div>
              </div>
            </div>
            <AnimatedHealthBar score={plan.financial_health_score} />
          </Card>
        </motion.div>

        {/* Client Profile & Financial Snapshot */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: isMobile ? 0 : 0.2 }}>
            <Card className="p-6 border-gray-200 dark:border-gray-800/60 h-full">
            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
              <User className="w-5 h-5 mr-2 text-blue-600" />
              Client Profile
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Age:</span>
                <span className="font-semibold text-gray-800 dark:text-gray-100">{plan.client_profile.age} years</span>
              </div>
              {plan.client_profile.occupation && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Occupation:</span>
                  <span className="font-semibold text-gray-800 dark:text-gray-100">{plan.client_profile.occupation}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Risk Profile:</span>
                <span className="font-semibold text-gray-800 dark:text-gray-100 capitalize">{plan.client_profile.risk_profile}</span>
              </div>
              {plan.client_profile.children && plan.client_profile.children.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Children:</span>
                  <span className="font-semibold text-gray-800 dark:text-gray-100">{plan.client_profile.children.length}</span>
                </div>
              )}
            </div>
          </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: isMobile ? 0 : 0.25 }}>
            <Card className="p-6 border-gray-200 dark:border-gray-800/60 h-full">
            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-green-600" />
              Financial Snapshot
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Monthly Income:</span>
                <span className="font-semibold text-gray-800 dark:text-gray-100">{formatCurrency(plan.financial_snapshot.monthly_income)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Monthly Expenses:</span>
                <span className="font-semibold text-gray-800 dark:text-gray-100">{formatCurrency(plan.financial_snapshot.monthly_expenses)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Monthly Surplus:</span>
                <span className="font-semibold text-green-600">{formatCurrency(plan.financial_snapshot.monthly_surplus)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Savings Rate:</span>
                <span className="font-semibold text-gray-800 dark:text-gray-100">{plan.financial_snapshot.savings_rate}%</span>
              </div>
            </div>
          </Card>
          </motion.div>
        </div>

        {/* Detailed Sections - Collapsible */}
        <motion.div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          <p className="text-sm text-blue-600 dark:text-blue-300 text-center flex items-center justify-center gap-2">
            <FileText className="w-4 h-4" /> Click on any section below to expand and view detailed information
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: isMobile ? 0 : 0.35 }}>
        <Accordion type="multiple" defaultValue={[]} className="space-y-4">
          
          {/* Financial Goals */}
          <AccordionItem value="goals" className="border-0">
            <Card className="glass dark:bg-gray-800">
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center">
                  <Target className="w-6 h-6 mr-2 text-purple-600" />
                  Financial Goals ({plan.goals.length})
                </h3>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
          <div className="space-y-4">
            {plan.goals.map((goal, index) => (
              <Card key={index} className="p-4 bg-purple-50 dark:bg-purple-900/10">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-bold text-gray-800 dark:text-gray-100 text-lg">{goal.name}</h4>
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold mt-1 inline-block ${getPriorityColor(goal.priority.toUpperCase())}`}>
                      {goal.priority.toUpperCase()} PRIORITY
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-purple-600">{formatCurrency(goal.monthly_sip_required)}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">per month</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                  <div>
                    <div className="text-gray-600 dark:text-gray-400">Current Target</div>
                    <div className="font-semibold text-gray-800 dark:text-gray-100">{formatCurrency(goal.current_target)}</div>
                  </div>
                  <div>
                    <div className="text-gray-600 dark:text-gray-400">Inflation Adjusted</div>
                    <div className="font-semibold text-gray-800 dark:text-gray-100">{formatCurrency(goal.inflation_adjusted_target)}</div>
                  </div>
                  <div>
                    <div className="text-gray-600 dark:text-gray-400">Timeline</div>
                    <div className="font-semibold text-gray-800 dark:text-gray-100">{goal.years_to_goal} years</div>
                  </div>
                  <div>
                    <div className="text-gray-600 dark:text-gray-400">Total Investment</div>
                    <div className="font-semibold text-gray-800 dark:text-gray-100">{formatCurrency(goal.total_investment)}</div>
                  </div>
                </div>

                {/* Year-by-Year Projections Toggle */}
                {goal.yearly_projections && goal.yearly_projections.length > 0 && (
                  <div className="mt-4 border-t pt-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpandedGoals({
                        ...expandedGoals,
                        [index]: !expandedGoals[index]
                      })}
                      className="w-full flex items-center justify-between hover:bg-purple-100 dark:hover:bg-purple-900/20"
                    >
                      <span className="font-semibold text-purple-600">
                        📊 Year-by-Year Projections ({goal.yearly_projections.length} years)
                      </span>
                      {expandedGoals[index] ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </Button>

                    {expandedGoals[index] && (
                      <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-purple-100 dark:bg-purple-900/30">
                            <tr>
                              <th className="px-3 py-2 text-left">Year</th>
                              <th className="px-3 py-2 text-left">Age</th>
                              <th className="px-3 py-2 text-right">Annual Investment</th>
                              <th className="px-3 py-2 text-right">Corpus Value</th>
                              <th className="px-3 py-2 text-right">Total Invested</th>
                              <th className="px-3 py-2 text-right">Gains</th>
                              <th className="px-3 py-2 text-right">Progress</th>
                            </tr>
                          </thead>
                          <tbody>
                            {goal.yearly_projections.map((projection, pIndex) => (
                              <tr 
                                key={pIndex}
                                className={`border-b ${pIndex % 5 === 0 ? 'bg-purple-50 dark:bg-purple-900/10' : ''}`}
                              >
                                <td className="px-3 py-2 font-medium">{projection.year}</td>
                                <td className="px-3 py-2">{projection.age}</td>
                                <td className="px-3 py-2 text-right">{formatCurrency(projection.annual_investment)}</td>
                                <td className="px-3 py-2 text-right font-semibold text-green-600">
                                  {formatCurrency(projection.corpus_value)}
                                </td>
                                <td className="px-3 py-2 text-right">{formatCurrency(projection.total_invested)}</td>
                                <td className="px-3 py-2 text-right text-blue-600">
                                  {formatCurrency(projection.gains)}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                      <div 
                                        className="bg-purple-600 h-2 rounded-full"
                                        style={{ width: `${Math.min(projection.progress_percentage, 100)}%` }}
                                      />
                                    </div>
                                    <span className="text-xs font-medium w-10 text-right">
                                      {projection.progress_percentage}%
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            ))}
            
            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-gray-800 dark:text-gray-100">Total Monthly SIP Required:</span>
                <span className="text-2xl font-bold text-blue-600">{formatCurrency(plan.total_monthly_sip_required)}</span>
              </div>
            </div>
          </div>
          </AccordionContent>
        </Card>
          </AccordionItem>

          {/* Insurance Analysis */}
          <AccordionItem value="insurance" className="border-0">
            <Card className="glass dark:bg-gray-800">
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center">
                  <Shield className="w-6 h-6 mr-2 text-blue-600" />
                  Insurance Gap Analysis
                </h3>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Life Insurance */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg">
              <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Life Insurance</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Current Cover:</span>
                  <span className="font-semibold">{formatCurrency(plan.insurance_analysis.life_insurance.current_cover)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Recommended:</span>
                  <span className="font-semibold">{formatCurrency(plan.insurance_analysis.life_insurance.recommended_cover)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Gap:</span>
                  <span className={`font-semibold ${plan.insurance_analysis.life_insurance.gap > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(plan.insurance_analysis.life_insurance.gap)}
                  </span>
                </div>
                <div className="flex items-center mt-2">
                  {plan.insurance_analysis.life_insurance.status === 'adequate' ? (
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                  )}
                  <span className="text-xs capitalize">{plan.insurance_analysis.life_insurance.status}</span>
                </div>
              </div>
            </div>

            {/* Health Insurance */}
            <div className="p-4 bg-green-50 dark:bg-green-900/10 rounded-lg">
              <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Health Insurance</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Current Cover:</span>
                  <span className="font-semibold">{formatCurrency(plan.insurance_analysis.health_insurance.current_cover)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Recommended:</span>
                  <span className="font-semibold">{formatCurrency(plan.insurance_analysis.health_insurance.recommended_cover)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Gap:</span>
                  <span className={`font-semibold ${plan.insurance_analysis.health_insurance.gap > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(plan.insurance_analysis.health_insurance.gap)}
                  </span>
                </div>
                <div className="flex items-center mt-2">
                  {plan.insurance_analysis.health_insurance.status === 'adequate' ? (
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                  )}
                  <span className="text-xs capitalize">{plan.insurance_analysis.health_insurance.status}</span>
                </div>
              </div>
            </div>
          </div>
          </AccordionContent>
        </Card>
          </AccordionItem>

          {/* Retirement Planning Analysis */}
          {plan.retirement_analysis && (
            <AccordionItem value="retirement" className="border-0">
              <Card className="glass dark:bg-gray-800">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center">
                    <TrendingUp className="w-6 h-6 mr-2 text-orange-600" />
                    Retirement Planning
                  </h3>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="p-4 bg-orange-50 dark:bg-orange-900/10 rounded-lg">
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Current Age</div>
                <div className="text-2xl font-bold text-gray-800 dark:text-gray-100">{plan.retirement_analysis.current_age}</div>
              </div>
              <div className="p-4 bg-orange-50 dark:bg-orange-900/10 rounded-lg">
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Retirement Age</div>
                <div className="text-2xl font-bold text-gray-800 dark:text-gray-100">{plan.retirement_analysis.retirement_age}</div>
              </div>
              <div className="p-4 bg-orange-50 dark:bg-orange-900/10 rounded-lg">
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Life Expectancy</div>
                <div className="text-2xl font-bold text-gray-800 dark:text-gray-100">{plan.retirement_analysis.life_expectancy}</div>
              </div>
              <div className="p-4 bg-orange-50 dark:bg-orange-900/10 rounded-lg">
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Years to Retirement</div>
                <div className="text-2xl font-bold text-orange-600">{plan.retirement_analysis.years_to_retirement}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-lg border-2 border-orange-200 dark:border-orange-800">
                <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Retirement Expenses</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Current Monthly Expenses:</span>
                    <span className="font-semibold">{formatCurrency(plan.retirement_analysis.current_monthly_expenses)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">At Retirement (Inflation-Adjusted):</span>
                    <span className="font-semibold text-orange-600">{formatCurrency(plan.retirement_analysis.retirement_monthly_expenses)}</span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border-2 border-green-200 dark:border-green-800">
                <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Retirement Corpus</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Required Corpus:</span>
                    <span className="font-bold text-green-600 text-lg">{formatCurrency(plan.retirement_analysis.required_corpus)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Monthly SIP Required:</span>
                    <span className="font-bold text-blue-600 text-lg">{formatCurrency(plan.retirement_analysis.monthly_sip_required)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Total Investment Needed:</span>
                    <span className="font-semibold">{formatCurrency(plan.retirement_analysis.total_investment_needed)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg text-sm text-gray-600 dark:text-gray-400">
              <strong>Note:</strong> Calculations assume {Number(plan.retirement_analysis.inflation_rate_assumed).toFixed(1)}% inflation 
              and {Number(plan.retirement_analysis.post_retirement_return_assumed).toFixed(1)}% post-retirement returns.
            </div>
                </AccordionContent>
              </Card>
            </AccordionItem>
        )}

        {/* Tax Planning */}
        {plan.tax_planning && plan.tax_planning.old_regime && plan.tax_planning.new_regime && (
          <AccordionItem value="tax" className="border-0">
            <Card className="glass dark:bg-gray-800">
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center">
                  <FileText className="w-6 h-6 mr-2 text-indigo-600" />
                  Tax Planning & Optimization
                </h3>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">

            {/* Tax Regime Comparison */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Old Regime */}
              <div className={`p-5 rounded-lg border-2 ${
                plan.tax_planning.recommended_regime === 'old' 
                  ? 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-500' 
                  : 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-700'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-lg text-gray-800 dark:text-gray-100">Old Tax Regime</h4>
                  {plan.tax_planning.recommended_regime === 'old' && (
                    <span className="px-3 py-1 bg-green-600 text-white text-xs rounded-full font-semibold">
                      RECOMMENDED
                    </span>
                  )}
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Gross Income:</span>
                    <span className="font-semibold">{formatCurrency(plan.tax_planning.old_regime.taxable_income)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Total Deductions:</span>
                    <span className="font-semibold text-green-600">-{formatCurrency(plan.tax_planning.old_regime.total_deductions)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-gray-600 dark:text-gray-400">Taxable Income:</span>
                    <span className="font-semibold">{formatCurrency(plan.tax_planning.old_regime.taxable_after_deductions)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t-2 border-gray-300">
                    <span className="font-bold text-gray-800 dark:text-gray-100">Tax Liability:</span>
                    <span className="font-bold text-red-600 text-lg">{formatCurrency(plan.tax_planning.old_regime.final_tax)}</span>
                  </div>
                  <div className="text-xs text-gray-500 text-right">
                    Effective Rate: {plan.tax_planning.old_regime.effective_rate}%
                  </div>
                </div>
              </div>

              {/* New Regime */}
              <div className={`p-5 rounded-lg border-2 ${
                plan.tax_planning.recommended_regime === 'new' 
                  ? 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-500' 
                  : 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-700'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-lg text-gray-800 dark:text-gray-100">New Tax Regime</h4>
                  {plan.tax_planning.recommended_regime === 'new' && (
                    <span className="px-3 py-1 bg-green-600 text-white text-xs rounded-full font-semibold">
                      RECOMMENDED
                    </span>
                  )}
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Gross Income:</span>
                    <span className="font-semibold">{formatCurrency(plan.tax_planning.new_regime.taxable_income)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Standard Deduction:</span>
                    <span className="font-semibold text-green-600">-{formatCurrency(plan.tax_planning.new_regime.standard_deduction)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-gray-600 dark:text-gray-400">Taxable Income:</span>
                    <span className="font-semibold">{formatCurrency(plan.tax_planning.new_regime.taxable_after_deduction)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t-2 border-gray-300">
                    <span className="font-bold text-gray-800 dark:text-gray-100">Tax Liability:</span>
                    <span className="font-bold text-red-600 text-lg">{formatCurrency(plan.tax_planning.new_regime.final_tax)}</span>
                  </div>
                  <div className="text-xs text-gray-500 text-right">
                    Effective Rate: {plan.tax_planning.new_regime.effective_rate}%
                  </div>
                </div>
              </div>
            </div>

            {/* Tax Savings Recommendation */}
            <div className="p-4 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-lg mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-700 dark:text-gray-300 font-medium">💡 Recommendation</div>
                  <div className="text-gray-800 dark:text-gray-100 font-semibold mt-1">{plan.tax_planning.recommendation}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-600 dark:text-gray-400">Potential Savings</div>
                  <div className="text-2xl font-bold text-green-600">{formatCurrency(plan.tax_planning.tax_savings_by_choosing_better)}</div>
                </div>
              </div>
            </div>

            {/* Available Deductions */}
            <div>
              <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Available Tax-Saving Deductions (Old Regime)</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-indigo-50 dark:bg-indigo-900/20">
                    <tr>
                      <th className="px-4 py-2 text-left">Section</th>
                      <th className="px-4 py-2 text-left">Investment Options</th>
                      <th className="px-4 py-2 text-right">Limit</th>
                      <th className="px-4 py-2 text-right">Tax Savings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(plan.tax_planning.deductions).map(([key, value]) => {
                      if (typeof value === 'object' && value.limit) {
                        return (
                          <tr key={key} className="border-b">
                            <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">
                              {key.toUpperCase().replace(/_/g, ' ')}
                            </td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">
                              {value.instruments.join(', ')}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold">{formatCurrency(value.limit)}</td>
                            <td className="px-4 py-3 text-right font-semibold text-green-600">{formatCurrency(value.potential_savings)}</td>
                          </tr>
                        );
                      }
                      return null;
                    })}
                    <tr className="bg-green-50 dark:bg-green-900/10 font-bold">
                      <td className="px-4 py-3" colSpan="3">Total Potential Tax Savings</td>
                      <td className="px-4 py-3 text-right text-green-600 text-lg">
                        {formatCurrency(plan.tax_planning.deductions.total_potential_tax_savings)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
              </AccordionContent>
            </Card>
          </AccordionItem>
        )}

        {/* Net Worth & Asset Allocation */}
        <AccordionItem value="allocation" className="border-0">
          <Card className="glass dark:bg-gray-800">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center">
                <PieChart className="w-6 h-6 mr-2 text-indigo-600" />
                Net Worth & Asset Allocation
              </h3>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
          
          {/* Net Worth Summary */}
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border-2 border-blue-200 dark:border-blue-800">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Net Worth Summary</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg border-2 border-blue-300 dark:border-blue-700">
                <div className="text-xs text-blue-600 dark:text-blue-400 font-semibold mb-1">Total Assets</div>
                <div className="text-base sm:text-lg font-bold text-gray-800 dark:text-gray-100 break-words">
                  {formatCurrency(plan.financial_snapshot.current_savings + plan.financial_snapshot.current_investments)}
                </div>
              </div>
              <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg border-2 border-red-300 dark:border-red-700">
                <div className="text-xs text-red-600 dark:text-red-400 font-semibold mb-1">Total Liabilities</div>
                <div className="text-base sm:text-lg font-bold text-gray-800 dark:text-gray-100 break-words">{formatCurrency(0)}</div>
              </div>
              <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg border-2 border-green-300 dark:border-green-700">
                <div className="text-xs text-green-600 dark:text-green-400 font-semibold mb-1">Net Worth</div>
                <div className="text-base sm:text-lg font-bold text-green-600 dark:text-green-400 break-words">
                  {formatCurrency(plan.financial_snapshot.current_savings + plan.financial_snapshot.current_investments)}
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Asset Allocation */}
          <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3">
            Recommended Asset Allocation Strategy
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Based on your risk profile: <span className="font-semibold text-indigo-600">{plan.client_profile.risk_profile.toUpperCase()}</span>
          </p>

          <div className="space-y-4">
            {/* Equity */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg border-l-4 border-blue-500">
              <div className="flex justify-between items-center mb-2">
                <h5 className="text-base font-bold text-gray-800 dark:text-gray-100">Equity</h5>
                <span className="text-2xl font-bold text-blue-600">{plan.asset_allocation.equity}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-3">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full" style={{width: `${plan.asset_allocation.equity}%`}}></div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="font-semibold text-gray-600 dark:text-gray-400">Investment Options:</span>
                  <p className="text-gray-700 dark:text-gray-300">Large Cap MFs, Index Funds</p>
                </div>
                <div>
                  <span className="font-semibold text-gray-600 dark:text-gray-400">Expected Return:</span>
                  <p className="text-blue-600 dark:text-blue-400 font-bold">12-15% p.a.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div className="p-2 bg-white dark:bg-gray-800 rounded">
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Risk:</span>
                  <span className="text-xs ml-1 text-gray-700 dark:text-gray-300">High</span>
                </div>
                <div className="p-2 bg-white dark:bg-gray-800 rounded">
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Target:</span>
                  <span className="text-xs ml-1 font-bold text-blue-600">
                    {formatCurrency((plan.financial_snapshot.current_savings + plan.financial_snapshot.current_investments) * plan.asset_allocation.equity / 100)}
                  </span>
                </div>
              </div>
            </div>

            {/* Debt */}
            <div className="p-4 bg-green-50 dark:bg-green-900/10 rounded-lg border-l-4 border-green-500">
              <div className="flex justify-between items-center mb-2">
                <h5 className="text-base font-bold text-gray-800 dark:text-gray-100">Debt</h5>
                <span className="text-2xl font-bold text-green-600">{plan.asset_allocation.debt}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-3">
                <div className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full" style={{width: `${plan.asset_allocation.debt}%`}}></div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="font-semibold text-gray-600 dark:text-gray-400">Investment Options:</span>
                  <p className="text-gray-700 dark:text-gray-300">Debt MFs, Corporate Bonds</p>
                </div>
                <div>
                  <span className="font-semibold text-gray-600 dark:text-gray-400">Expected Return:</span>
                  <p className="text-green-600 dark:text-green-400 font-bold">7-9% p.a.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div className="p-2 bg-white dark:bg-gray-800 rounded">
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Risk:</span>
                  <span className="text-xs ml-1 text-gray-700 dark:text-gray-300">Low-Medium</span>
                </div>
                <div className="p-2 bg-white dark:bg-gray-800 rounded">
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Target:</span>
                  <span className="text-xs ml-1 font-bold text-green-600">
                    {formatCurrency((plan.financial_snapshot.current_savings + plan.financial_snapshot.current_investments) * plan.asset_allocation.debt / 100)}
                  </span>
                </div>
              </div>
            </div>

            {/* Gold */}
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/10 rounded-lg border-l-4 border-yellow-500">
              <div className="flex justify-between items-center mb-2">
                <h5 className="text-base font-bold text-gray-800 dark:text-gray-100">Gold</h5>
                <span className="text-2xl font-bold text-yellow-600">{plan.asset_allocation.gold}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-3">
                <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 h-3 rounded-full" style={{width: `${plan.asset_allocation.gold}%`}}></div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="font-semibold text-gray-600 dark:text-gray-400">Investment Options:</span>
                  <p className="text-gray-700 dark:text-gray-300">Gold ETFs, Sovereign Gold Bonds</p>
                </div>
                <div>
                  <span className="font-semibold text-gray-600 dark:text-gray-400">Expected Return:</span>
                  <p className="text-yellow-600 dark:text-yellow-400 font-bold">8-10% p.a.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div className="p-2 bg-white dark:bg-gray-800 rounded">
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Risk:</span>
                  <span className="text-xs ml-1 text-gray-700 dark:text-gray-300">Medium</span>
                </div>
                <div className="p-2 bg-white dark:bg-gray-800 rounded">
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Target:</span>
                  <span className="text-xs ml-1 font-bold text-yellow-600">
                    {formatCurrency((plan.financial_snapshot.current_savings + plan.financial_snapshot.current_investments) * plan.asset_allocation.gold / 100)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Rebalancing Tip */}
          <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 dark:border-yellow-600 rounded-lg">
            <p className="text-xs text-yellow-800 dark:text-yellow-300">
              <span className="font-semibold">💡 Portfolio Rebalancing:</span> Review and rebalance your portfolio every 6-12 months to maintain optimal allocation and maximize returns.
            </p>
          </div>
            </AccordionContent>
          </Card>
        </AccordionItem>

        {/* Action Plan */}
        <AccordionItem value="action" className="border-0">
          <Card className="glass dark:bg-gray-800">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center">
                <TrendingUp className="w-6 h-6 mr-2 text-green-600" />
                Action Plan
              </h3>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
          <div className="space-y-3">
            {plan.action_plan.map((action, index) => (
              <Card key={index} className="p-4 bg-gray-50 dark:bg-gray-900/20">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs px-2 py-1 rounded-full font-semibold ${getPriorityColor(action.priority)}`}>
                        {action.priority}
                      </span>
                      <h4 className="font-bold text-gray-800 dark:text-gray-100">{action.action}</h4>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{action.description}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>⏱️ {action.timeline}</span>
                      <span>💰 {formatCurrency(action.monthly_allocation)}/month</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
            </AccordionContent>
          </Card>
        </AccordionItem>

      </Accordion>
      </motion.div>
      </motion.div>
      <Footer />
    </div>
  );
}
